import { useEffect, useRef, useState, useCallback } from 'react';
import { getToken } from '../helper/tokenHelper';

// Base URL resolution (same as apiEnviornment.js)
const getBaseURL = () => {
  // Try to get from environment variables
  let envBaseURL = process.env.REACT_APP_API_BASE_URL;
  
  // Try Vite env if available (via try-catch to avoid syntax errors)
  if (!envBaseURL) {
    try {
      // eslint-disable-next-line no-undef
      if (typeof VITE_API_BASE_URL !== 'undefined') {
        // eslint-disable-next-line no-undef
        envBaseURL = VITE_API_BASE_URL;
      }
    } catch (e) {
      // Vite env not available, continue with defaults
    }
  }
  
  // Fallback to defaults
  if (!envBaseURL) {
    envBaseURL = process.env.NODE_ENV === 'development'
      ? 'http://localhost:3000/api/v1'
      : 'https://backend-zeta-five-21.vercel.app/api/v1';
  }
  
  return envBaseURL;
};

/**
 * useSSE - Hook for subscribing to Server-Sent Events
 * 
 * @param {string} endpoint - SSE endpoint path (e.g., '/api/v1/sse/bookings')
 * @param {Object} options - Configuration options
 * @param {Function} options.onBookingUpdate - Callback for booking-update events
 * @param {Function} options.onBookingCreated - Callback for booking-created events
 * @param {Function} options.onBookingDeleted - Callback for booking-deleted events
 * @param {Function} options.onActivityLog - Callback for activity-log events
 * @param {boolean} options.enabled - Whether the connection should be active (default: true)
 * 
 * @returns {Object} { connected: boolean, error: Error|null }
 */
export function useSSE(endpoint, options = {}) {
  const {
    onBookingUpdate,
    onBookingCreated,
    onBookingDeleted,
    onActivityLog,
    enabled = true,
  } = options;

  const [connected, setConnected] = useState(false);
  const [error, setError] = useState(null);
  const eventSourceRef = useRef(null);
  const reconnectTimeoutRef = useRef(null);

  // Memoize callbacks to prevent unnecessary reconnections
  const onBookingUpdateRef = useRef(onBookingUpdate);
  const onBookingCreatedRef = useRef(onBookingCreated);
  const onBookingDeletedRef = useRef(onBookingDeleted);
  const onActivityLogRef = useRef(onActivityLog);

  useEffect(() => {
    onBookingUpdateRef.current = onBookingUpdate;
    onBookingCreatedRef.current = onBookingCreated;
    onBookingDeletedRef.current = onBookingDeleted;
    onActivityLogRef.current = onActivityLog;
  }, [onBookingUpdate, onBookingCreated, onBookingDeleted, onActivityLog]);

  const cleanup = useCallback(() => {
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }
    if (eventSourceRef.current) {
      eventSourceRef.current.close();
      eventSourceRef.current = null;
    }
  }, []);

  useEffect(() => {
    if (!enabled || !endpoint) {
      cleanup();
      setConnected(false);
      return;
    }

    const token = getToken();
    if (!token) {
      setError(new Error('No authentication token available'));
      setConnected(false);
      return;
    }

    // Build full URL with token as query parameter
    const baseURL = getBaseURL();
    const fullURL = `${baseURL}${endpoint}?token=${encodeURIComponent(token)}`;

    try {
      const eventSource = new EventSource(fullURL);
      eventSourceRef.current = eventSource;

      eventSource.onopen = () => {
        setConnected(true);
        setError(null);
      };

      eventSource.onerror = (err) => {
        setConnected(false);
        setError(err);
        // EventSource automatically reconnects, so we don't need to manually reconnect
      };

      // Listen for booking-update events
      eventSource.addEventListener('booking-update', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onBookingUpdateRef.current) {
            onBookingUpdateRef.current(data);
          }
        } catch (parseError) {
          console.error('Failed to parse booking-update event:', parseError);
        }
      });

      // Listen for booking-created events
      eventSource.addEventListener('booking-created', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onBookingCreatedRef.current) {
            onBookingCreatedRef.current(data);
          }
        } catch (parseError) {
          console.error('Failed to parse booking-created event:', parseError);
        }
      });

      // Listen for booking-deleted events
      eventSource.addEventListener('booking-deleted', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onBookingDeletedRef.current) {
            onBookingDeletedRef.current(data);
          }
        } catch (parseError) {
          console.error('Failed to parse booking-deleted event:', parseError);
        }
      });

      // Listen for activity-log events
      eventSource.addEventListener('activity-log', (event) => {
        try {
          const data = JSON.parse(event.data);
          if (onActivityLogRef.current) {
            onActivityLogRef.current(data);
          }
        } catch (parseError) {
          console.error('Failed to parse activity-log event:', parseError);
        }
      });

    } catch (err) {
      setError(err);
      setConnected(false);
    }

    return cleanup;
  }, [endpoint, enabled, cleanup]);

  return { connected, error };
}
